import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Autoplay } from 'swiper/modules';
import { Swiper as SwiperClass } from 'swiper/types';
import isObject from 'lodash/isObject';
import styles from '@components/FurnituresCarousel/FurnitureCarousel.styles';
import theme from '@styles/theme';
import Heading from '@components/Heading/Heading';
import { TCarouselSlides, ECarouselType, TCaruselSlide } from '@components/Carousel/typescript';
import GetImageAlt from '@utils/getImageAlt';
import prepareURLByCMSLink from '@utils/prepareURLByCMSLink';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';

type PropsType = {
  slides: TCarouselSlides;
  type: ECarouselType;
};

const FurnitureCarousel = ({ slides, type }: PropsType) => {
  const sliderMarginBreakpoint = theme.breakpoints[3].replace('px', '');
  const isPackshot = type === ECarouselType.packshot;
  const defaultColor = isPackshot ? theme.rawColors.neutralBlack : theme.rawColors.neutralWhite;
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass>();

  const renderSlides = (slider: TCaruselSlide) => (
    <div sx={styles.slideStyles} data-cy="SwiperSlide">
      {!isPackshot && <div className="hoverOverlap" />}
      {slider.image &&
        (isPackshot ? (
          <div sx={styles.packshotImageWrapper}>
            <ResponsiveImage
              image={slider.image}
              blur={slider.blur}
              alt={GetImageAlt(slider.image) || slider.title}
              objectFit="contain"
            />
          </div>
        ) : (
          <ResponsiveImage
            image={slider.image}
            blur={slider.blur}
            alt={GetImageAlt(slider.image) || slider.title}
            sx={styles.imageSlide}
            objectFit="cover"
          />
        ))}
      <div sx={styles.sliderTitle(slider.titleColor || defaultColor, isPackshot)}>
        <Heading h={4} headingType={isPackshot ? 4 : 2} mobile color={slider.titleColor || defaultColor}>
          {slider.title}
        </Heading>
      </div>
    </div>
  );

  // WCAG 2.1.1 Keyboard - automatyczne przełączanie slajdu gdy focus trafia na element poza widocznym obszarem
  useEffect(() => {
    if (!swiperInstance) return;

    const handleFocusIn = (event: FocusEvent) => {
      const focusedElement = event.target as HTMLElement;
      if (!focusedElement) return;

      const sliderContainer = swiperInstance.el;
      if (!sliderContainer || !sliderContainer.contains(focusedElement)) return;

      const slideContainingFocus = focusedElement.closest('.swiper-slide') as HTMLElement;
      if (!slideContainingFocus) return;

      const slideRect = slideContainingFocus.getBoundingClientRect();
      const containerRect = sliderContainer.getBoundingClientRect();
      const isVisible =
        slideRect.left >= containerRect.left &&
        slideRect.right <= containerRect.right &&
        slideRect.top >= containerRect.top &&
        slideRect.bottom <= containerRect.bottom;

      if (isVisible) return;

      const slideIndex = Array.from(swiperInstance.slides).indexOf(slideContainingFocus);
      if (slideIndex !== -1) {
        swiperInstance.slideTo(slideIndex);
      }
    };

    const sliderContainer = swiperInstance.el;
    sliderContainer.addEventListener('focusin', handleFocusIn);

    return () => {
      sliderContainer.removeEventListener('focusin', handleFocusIn);
    };
  }, [swiperInstance, slides.length]);

  if (slides.length < 1) return <></>;

  return (
    <div sx={styles.sliderContainer} data-cy="MainCategoriesSlider">
      <Swiper
        slidesPerView="auto"
        spaceBetween={4}
        allowTouchMove
        freeMode
        modules={[FreeMode, Autoplay]}
        loop
        onSwiper={(swiper: SwiperClass) => setSwiperInstance(swiper)}
        autoplay={{
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        speed={20000}
        breakpoints={{
          [sliderMarginBreakpoint]: {
            spaceBetween: 24,
          },
        }}
      >
        {slides.map((slider) => {
          const sliderLink = isObject(slider.link) ? prepareURLByCMSLink(slider.link) : slider.link;

          return (
            <SwiperSlide key={slider.itemId}>
              {sliderLink ? <Link href={sliderLink || '/'}>{renderSlides(slider)}</Link> : renderSlides(slider)}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default FurnitureCarousel;
